import { request, url } from "@frontend/lib/http";

import type {
  Amendment,
  AmendmentBatchResult,
  AmendmentEmployeePage,
  AmendmentHistory,
  AmendmentSortDirection,
  AmendmentStatusChoice,
} from "../types";

type RequestOptions = {
  signal?: AbortSignal;
};

export type AmendmentEmployeeQuery = {
  search?: string;
  responsable?: number | null;
  status?: string | null;
  direction?: AmendmentSortDirection;
  page?: number;
  perPage?: number;
};

export type AmendmentApi = {
  fetchEmployees(
    year: number,
    query: AmendmentEmployeeQuery,
    options?: RequestOptions,
  ): Promise<AmendmentEmployeePage>;
  fetchStatuses(options?: RequestOptions): Promise<AmendmentStatusChoice[]>;
  generate(
    year: number,
    employeeId: number,
    generatedOn?: string,
    options?: RequestOptions,
  ): Promise<Amendment>;
  generateBatch(
    year: number,
    employeeIds: number[],
    generatedOn?: string,
    options?: RequestOptions,
  ): Promise<AmendmentBatchResult>;
  previewBundle(
    year: number,
    employeeIds: number[],
    options?: RequestOptions,
  ): Promise<AmendmentBatchResult>;
  changeStatus(
    amendmentId: number,
    status: string,
    options?: RequestOptions,
  ): Promise<Amendment>;
  fetchHistory(
    amendmentId: number,
    options?: RequestOptions,
  ): Promise<AmendmentHistory>;
  documentUrl(amendmentId: number): string;
  exportUrl(year: number): string;
  downloadBundle(year: number, employeeIds: number[]): Promise<void>;
};

export const amendmentApi: AmendmentApi = {
  fetchEmployees(year, query, options) {
    return request({
      route: "api_employee_contract_amendments_list",
      query: {
        year,
        search: query.search || undefined,
        responsable: query.responsable ?? undefined,
        status: query.status || undefined,
        direction: query.direction,
        page: query.page,
        perPage: query.perPage,
      },
      ...options,
    });
  },

  fetchStatuses(options) {
    return request({
      route: "api_employee_contract_amendment_statuses",
      ...options,
    });
  },

  generate(year, employeeId, generatedOn, options) {
    return request({
      route: "api_employee_contract_amendments_generate",
      params: { employeeId },
      query: { year },
      method: "POST",
      body: { amendmentGenerationInput: { generatedOn } },
      ...options,
    });
  },

  generateBatch(year, employeeIds, generatedOn, options) {
    return request({
      route: "api_employee_contract_amendments_batch",
      query: { year },
      method: "POST",
      body: { amendmentBatchGenerationInput: { employeeIds, generatedOn } },
      ...options,
    });
  },

  previewBundle(year, employeeIds, options) {
    return request({
      route: "api_employee_contract_amendments_bundle_preview",
      query: { year },
      method: "POST",
      body: { amendmentSelectionInput: { employeeIds } },
      ...options,
    });
  },

  changeStatus(amendmentId, status, options) {
    return request({
      route: "api_employee_contract_amendments_change_status",
      params: { amendmentId },
      method: "PUT",
      body: { amendmentStatusInput: { status } },
      ...options,
    });
  },

  fetchHistory(amendmentId, options) {
    return request({
      route: "api_employee_contract_amendments_history",
      params: { amendmentId },
      ...options,
    });
  },

  documentUrl(amendmentId) {
    return url("api_employee_contract_amendments_document", { amendmentId });
  },

  exportUrl(year) {
    return url("api_employee_contract_amendments_export", {}, { year });
  },

  async downloadBundle(year, employeeIds) {
    const response = await fetch(
      url("api_employee_contract_amendments_bundle", {}, { year }),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amendmentSelectionInput: { employeeIds } }),
      },
    );

    if (!response.ok) {
      throw new Error(String(response.status));
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = `amendments_${year}.zip`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  },
};
